import { isString } from 'fitify-types/src/type-guards/common'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { BreadcrumbJsonLd, VideoJsonLd } from 'next-seo'

import { Cta } from '@/components/exercises/Cta/Cta'
import { StyledContainer } from '@/components/exercises/Exercise/Container.Styled'
import { Hero } from '@/components/exercises/Exercise/Hero/Hero'
import { Instructions } from '@/components/exercises/Exercise/Instructions/Instructions'
import { Muscles } from '@/components/exercises/Exercise/Muscles/Muscles'
import { RelatedExercises } from '@/components/exercises/Exercise/RelatedExercises/RelatedExercises'
import { useExercise } from '@/components/exercises/Exercise/useExercise'
import Layout from '@/components/layouts/Layout'
import { type ExerciseItem, type ExerciseListItem } from '@/utils/exercises'
import {
  getAllExercisePaths,
  getExerciseByToolAndSlug,
  getRelatedExercises,
} from '@/utils/exercises.server'
import { getStaticPropsTranslations } from '@/utils/i18n'

type ExercisePageProps = {
  exercise: ExerciseItem
  related: ExerciseListItem[]
}

const ExercisePage: NextPage<ExercisePageProps> = ({ exercise, related }) => {
  const { breadcrumbs, breadcrumbsJsonLd, description, title } =
    useExercise(exercise)

  return (
    <Layout
      seo={{
        title,
        description,
        openGraph: {
          title,
          description,
          type: 'article',
          images: exercise.thumbnail
            ? [
                {
                  url: exercise.thumbnail,
                  alt: exercise.title,
                  width: 640,
                  height: 360,
                },
              ]
            : undefined,
        },
      }}
    >
      <BreadcrumbJsonLd itemListElements={breadcrumbsJsonLd} />

      {exercise.video && (
        <VideoJsonLd
          name={`${exercise.title} – Exercise Demo`}
          description={description}
          thumbnailUrls={exercise.thumbnail ? [exercise.thumbnail] : []}
          contentUrl={exercise.video}
          uploadDate={new Date().toISOString()}
        />
      )}

      <StyledContainer>
        <Hero breadcrumbs={breadcrumbs} exercise={exercise} />
        <Muscles exercise={exercise} />
        <Instructions exercise={exercise} />
        <Cta />
        <RelatedExercises exercises={related} />
      </StyledContainer>
    </Layout>
  )
}

export default ExercisePage

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const exercisePaths = await getAllExercisePaths()

  const paths = exercisePaths.flatMap(({ tool, slug }) =>
    (locales || ['en']).map((locale) => ({ params: { tool, slug }, locale }))
  )

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<ExercisePageProps> = async ({
  params,
  locale,
}) => {
  const tool = params?.tool
  const slug = params?.slug

  if (!isString(tool) || !isString(slug)) {
    return { notFound: true }
  }

  const exercise = await getExerciseByToolAndSlug(tool, slug)

  if (!exercise) {
    return { notFound: true }
  }

  const related = await getRelatedExercises(exercise, 6)

  return {
    props: {
      exercise,
      related,
      ...(await getStaticPropsTranslations(locale || 'en', ['instructions'])),
    },
  }
}
